import React, { useState, useEffect } from 'react';
import { 
  Modal, Descriptions, Tag, Spin, notification, Button,
  Tabs, Image, Empty, Divider, Statistic, Card, Row, Col,
  Badge,
  Table,
  Tooltip
} from 'antd';
import {
  DollarOutlined, ShoppingOutlined, TagOutlined,
  InfoCircleOutlined, CheckCircleOutlined, CloseCircleOutlined,
  DeleteOutlined
} from '@ant-design/icons';
import productService from '../../../../services/productService';
import categoryService from '../../../../services/categoryService';
import config from '../../../../config';
import userService from '../../../../services/userService';
import { findSchedoxService, getSchedoxServiceItems } from '../../../../utils/schedoxCatalog';

const { TabPane } = Tabs;

const resolveAssetUrl = (value) => {
  if (!value) return '';
  const cleanValue = String(value).replace(/^(\.\.\/)+/, '').replace(/^\/+/, '');
  if (/^https?:\/\//i.test(cleanValue)) return cleanValue;
  return `${config.APP_URL}/${cleanValue}`;
};

/**
 * View Product Modal Component
 */
const ViewProductModal = ({ visible, productId, onCancel, users, schedoxCatalog }) => {
  const [product, setProduct] = useState(null);
  const [category, setCategory] = useState(null);
  const [loading, setLoading] = useState(false);
  const [customPrices, setCustomPrices] = useState([]);
   // Custom price columns for table
   const customPriceColumns = [
    {
      title: 'Customer',
      dataIndex: 'username',
      key: 'username',
    },
    {
      title: 'Price',
      dataIndex: 'price',
      key: 'price',
      render: (price) => `£${parseFloat(price).toFixed(2)}`
    },
     
  ];
  
  // Fetch product data when modal opens
  useEffect(() => {
    if (visible && productId) {

      fetchProductData();
    }
    
    // Clear data when modal closes
    return () => {
      setProduct(null);
      setCategory(null);
    };
  }, [visible, productId]);

  const fetchProductData = async () => {
    try {
      setLoading(true);
      const response = await productService.getProductById(productId);
      const productData = response.data;
      setProduct(productData);
      
      // Fetch category details
      if (productData.category_id) {
        try {
          const categoryResponse = await categoryService.getCategoryById(productData.category_id);
          setCategory(categoryResponse.data);
        } catch (error) {
          console.error('Error fetching category:', error);
        }
      }
      if (productData.custom_prices && productData.custom_prices.length > 0) {
        setCustomPrices(productData.custom_prices.map(cp => ({
          key: cp.id,
          userId: cp.user_id,
          price: parseFloat(cp.price),
          username: cp.user?.name || users?.find(u => u.id === cp.user_id)?.name || `User #${cp.user_id}`
        })));
      } else {
        setCustomPrices([]);
      }
      
    } catch (error) {
      notification.error({
        message: 'Error loading product',
        description: error.message || 'Failed to load product data'
      });
    } finally {
      setLoading(false);
    }
  };
  
  // Helper function to format price
  const formatPrice = (price) => {
    return price ? `£${parseFloat(price).toFixed(2)}` : '£0.00';
  };
  
  // Helper function to get stock status
  const getStockStatus = (quantity) => {
    const qty = parseInt(quantity || 0);
    if (qty <= 0) {
      return <Badge status="error" text="Out of Stock" />;
    } else if (qty <= 10) {
      return <Badge status="warning" text={`Low Stock (${qty})`} />;
    } else {
      return <Badge status="success" text={`In Stock (${qty})`} />;
    }
  };
  
  // Helper function to check if product is a rental product
  const isRentalProduct = (product) => {
    return product && (
      parseFloat(product.priceone) > 0 || 
      parseFloat(product.pricetwo) > 0 || 
      parseFloat(product.pricethree) > 0 || 
      parseFloat(product.pricefour) > 0
    );
  };

  const mappedSchedoxService = findSchedoxService(
    schedoxCatalog,
    product?.schedox_service_id
  );
  const mappedSchedoxItem = getSchedoxServiceItems(
    schedoxCatalog,
    product?.schedox_service_id
  ).find((item) => (
    Number(item.service_item_id) === Number(product?.schedox_service_item_id)
  ));
  const schedoxServiceLabel = mappedSchedoxService
    ? mappedSchedoxService.name
    : product?.schedox_service_id
      ? `Mapped service #${product.schedox_service_id} is unavailable`
      : 'Legacy property/default service fallback';
  const schedoxItemLabel = mappedSchedoxItem
    ? ` / ${mappedSchedoxItem.name}`
    : product?.schedox_service_item_id
      ? ` / item #${product.schedox_service_item_id} is unavailable`
      : '';
  
  return (
    <Modal
      title="View Product Details"
      open={visible}
      onCancel={onCancel}
      width={800}
      footer={[
        <Button key="back" onClick={onCancel}>
          Close
        </Button>
      ]}
    >
      <Spin spinning={loading}>
        {product ? (
          <Tabs defaultActiveKey="details">
            <TabPane tab="Details" key="details">
              <div className="flex flex-col md:flex-row gap-6">
                {/* Product Image */}
                <div className="md:w-1/3">
                  <div className="bg-gray-50 p-4 rounded-lg mb-4">
                    {product.image ? (
                      <Image
                        src={resolveAssetUrl(product.image_url || product.image)}
                        alt={product.product_title}
                        className="rounded-md"
                        fallback="/fallback-image.png"
                      />
                    ) : (
                      <Empty 
                        description="No Image" 
                        image={Empty.PRESENTED_IMAGE_SIMPLE} 
                        className="py-8"
                      />
                    )}
                  </div>
                </div>
                
                {/* Product Information */}
                <div className="md:w-2/3">
                  <Descriptions 
                    title={
                      <div className="flex items-center gap-2">
                        <span>{product.product_title}</span>
                        {product.is_pack === 1 || product.is_pack === '1' ? (
                          <Tag color="purple">Pack</Tag>
                        ) : null}
                        <Tag color={product.status === '1' ? 'green' : 'red'}>
                          {product.status === '1' ? 'Active' : 'Inactive'}
                        </Tag>
                      </div>
                    } 
                    bordered 
                    layout="vertical"
                    column={{ xxl: 2, xl: 2, lg: 2, md: 2, sm: 1, xs: 1 }}
                  >
                    <Descriptions.Item label="Product Code">
                      {product.product_code || 'N/A'}
                    </Descriptions.Item>
                    
                    <Descriptions.Item label="Category">
                      {category ? (
                        <Tag color="blue">{category.category_title}</Tag>
                      ) : `ID: ${product.category_id}`}
                    </Descriptions.Item>
                    
                    <Descriptions.Item label="Stock Status">
                      {getStockStatus(product.quantity)}
                    </Descriptions.Item>

                    <Descriptions.Item label="Schedox Integration">
                      <Tag color={product.sync_to_schedox ? 'green' : 'default'}>
                        {product.sync_to_schedox ? 'Enabled' : 'Disabled'}
                      </Tag>
                      {product.sync_to_schedox ? (
                        <div className="mt-1 text-xs text-gray-600">
                          {schedoxServiceLabel}
                          {schedoxItemLabel}
                        </div>
                      ) : null}
                    </Descriptions.Item>
                    
                    <Descriptions.Item label="Created At">
                      {new Date(product.created_at).toLocaleString()}
                    </Descriptions.Item>

                    <Descriptions.Item label="Description" span={2}>
                      {product.product_description || 'No description available'}
                    </Descriptions.Item>
                  </Descriptions>
                </div>
              </div>
            </TabPane>
            
            <TabPane tab="Pricing" key="pricing">
              <Card className="mb-6">
                <Row gutter={[16, 16]}>
                  {isRentalProduct(product) ? (
                    <>
                      <Col xs={24}>
                        <div className="mb-4">
                          <Tag color="blue">Rental Product</Tag>
                          <p className="text-sm text-gray-500 mt-2">
                            This product has rental pricing for different durations.
                          </p>
                        </div>
                      </Col>
                      
                      <Col xs={24} sm={12} md={6}>
                        <Statistic 
                          title="3 Days"
                          value={formatPrice(product.priceone)}
                          valueStyle={{ color: '#1890ff' }}
                          prefix={<DollarOutlined />}
                        />
                      </Col>
                      
                      <Col xs={24} sm={12} md={6}>
                        <Statistic 
                          title="1 Week"
                          value={formatPrice(product.pricetwo)}
                          valueStyle={{ color: '#1890ff' }}
                          prefix={<DollarOutlined />}
                        />
                      </Col>
                      
                      <Col xs={24} sm={12} md={6}>
                        <Statistic 
                          title="2 Weeks"
                          value={formatPrice(product.pricethree)}
                          valueStyle={{ color: '#1890ff' }}
                          prefix={<DollarOutlined />}
                        />
                      </Col>
                      
                      <Col xs={24} sm={12} md={6}>
                        <Statistic 
                          title="1 Month"
                          value={formatPrice(product.pricefour)}
                          valueStyle={{ color: '#1890ff' }}
                          prefix={<DollarOutlined />}
                        />
                      </Col>
                    </>
                  ) : (
                    <>
                      <Col xs={24} md={12}>
                        <Statistic 
                          title="Selling Price"
                          value={formatPrice(product.product_price)}
                          valueStyle={{ color: '#3f8600' }}
                          prefix={<DollarOutlined />}
                        />
                      </Col>
                    </>
                  )}
                  
                  <Col xs={24} md={12}>
                    <Statistic 
                      title="Cost Price"
                      value={formatPrice(product.cost_price)}
                      valueStyle={{ color: '#cf1322' }}
                      prefix={<DollarOutlined />}
                    />
                  </Col>
                </Row>
              </Card>
              
              {product.product_price && product.cost_price && (
                <Card title="Profit Margin">
                  <Row gutter={[16, 16]}>
                    <Col xs={24} md={8}>
                      <Statistic 
                        title="Profit Amount"
                        value={formatPrice(product.product_price - product.cost_price)}
                        valueStyle={{ color: '#3f8600' }}
                        prefix={<DollarOutlined />}
                      />
                    </Col>
                    
                    <Col xs={24} md={8}>
                      <Statistic 
                        title="Margin Percentage"
                        value={`${(((product.product_price - product.cost_price) / product.product_price) * 100).toFixed(2)}%`}
                        valueStyle={{ color: '#3f8600' }}
                      />
                    </Col>
                    
                    <Col xs={24} md={8}>
                      <Statistic 
                        title="Markup Percentage"
                        value={`${(((product.product_price - product.cost_price) / product.cost_price) * 100).toFixed(2)}%`}
                        valueStyle={{ color: '#3f8600' }}
                      />
                    </Col>
                  </Row>
                </Card>
              )}
            </TabPane>
            <TabPane tab="Custom Pricing" key="customPricing">
                <div className="mb-6">
                  <Table
                    columns={customPriceColumns}
                    dataSource={customPrices}
                    rowKey="key"
                    pagination={false}
                    size="small"
                    locale={{ emptyText: 'No custom prices set' }}
                  />
                </div>
              </TabPane>
            <TabPane tab="Inventory" key="inventory">
              <Card className="mb-6">
                <Row gutter={[16, 16]}>
                  <Col xs={24} md={8}>
                    <Statistic 
                      title="In Stock"
                      value={product.quantity || 0}
                      valueStyle={{ 
                        color: parseInt(product.quantity) > 0 ? '#3f8600' : '#cf1322'
                      }}
                      prefix={<ShoppingOutlined />}
                    />
                  </Col>
                  
                  <Col xs={24} md={8}>
                    <Statistic 
                      title="Status"
                      value={parseInt(product.quantity) > 0 ? 'Available' : 'Out of Stock'}
                      valueStyle={{ 
                        color: parseInt(product.quantity) > 0 ? '#3f8600' : '#cf1322'
                      }}
                      prefix={parseInt(product.quantity) > 0 ? 
                        <CheckCircleOutlined /> : <CloseCircleOutlined />}
                    />
                  </Col>
                  
                  {product.actualqtyinwh && (
                    <Col xs={24} md={8}>
                      <Statistic 
                        title="Actual Quantity in Warehouse"
                        value={product.actualqtyinwh}
                        valueStyle={{ color: '#1890ff' }}
                        prefix={<ShoppingOutlined />}
                      />
                    </Col>
                  )}
                </Row>
              </Card>
              
              <Divider orientation="left">Additional Information</Divider>
              
              <Descriptions bordered column={1}>
                {product.is_pack === 1 || product.is_pack === '1' ? (
                  <Descriptions.Item label="Pack Type">
                    <Tag color="purple">This product is a pack</Tag>
                  </Descriptions.Item>
                ) : null}
                
                <Descriptions.Item label="Last Updated">
                  {new Date(product.updated_at).toLocaleString()}
                </Descriptions.Item>
                
                {product.pack_items_id && (
                  <Descriptions.Item label="Pack Items">
                    {JSON.parse(product.pack_items_id).map((itemId) => (
                      <Tag key={itemId} color="blue">{`Item ID: ${itemId}`}</Tag>
                    ))}
                  </Descriptions.Item>
                )}
              </Descriptions>
            </TabPane>
          </Tabs>
        ) : (
          <div className="flex justify-center items-center h-64">
            <Spin size="large" />
            <p className="ml-3 text-gray-500">Loading product data...</p>
          </div>
        )}
      </Spin>
    </Modal>
  );
};

export default ViewProductModal;
